import { faArrowToTop } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, FormError, Spinner } from '@kcftech/design-system';
import {
    FileNameTooLongError,
    FileTooLargeError,
    UnsupportedFileTypeError,
} from 'pages/Settings/FileLibrary/shared';
import { useMemo } from 'react';
import * as React from 'react';

type UploadingSpinnerAndTextProp = Readonly<{
    className: string;
}>;

function UploadingSpinnerAndText({ className }: UploadingSpinnerAndTextProp) {
    return (
        <div className={className}>
            <Spinner size="1x" className="inline-block mr-10" />
            Uploading...
        </div>
    );
}

export type FileUploadButtonProps = Readonly<{
    onUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
    buttonLabel: string;
    buttonSpacing?: 'none' | 'medium' | 'compact' | 'large' | undefined;
    error?: unknown;
    isError?: boolean;
    isMultiFile?: boolean;
    isUploading: boolean;
    fileInputRef: React.RefObject<HTMLInputElement> | null;
    onButtonClick?: () => void;
    absolutePositionedSpinner?: boolean;
}>;

export function FileUploadButton({
    onUpload,
    buttonLabel,
    buttonSpacing = 'compact',
    error,
    isError,
    isMultiFile,
    isUploading,
    fileInputRef,
    onButtonClick,
    absolutePositionedSpinner,
}: FileUploadButtonProps) {
    const uploadError = useMemo(() => {
        if (isError) {
            if (error instanceof UnsupportedFileTypeError) {
                return 'Unsupported file type. Please upload a .PNG, .JPG, or .WEBP file.';
            }
            if (error instanceof FileTooLargeError) {
                return 'File is too large. Please upload a file smaller than 10MB.';
            }
            if (error instanceof FileNameTooLongError) {
                return 'File name is too long.';
            }
            return 'Could not upload file';
        }
        return undefined;
    }, [error, isError]);

    return (
        <div style={{ display: absolutePositionedSpinner ? 'inline-block' : 'block' }}>
            <div className="flex align-center">
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple={isMultiFile}
                    accept=".png,.jpg,.jpeg,.webp"
                    // We must use opacity to hide the input here, because "display: none"
                    // and "visibility: hidden" would also semantically hide the input and
                    // render it inaccessible.
                    style={{ opacity: 0, width: '0.1px' }}
                    onChange={onUpload}
                    disabled={isUploading}
                />
                <Button
                    type="button"
                    appearance="primary"
                    icon={<FontAwesomeIcon icon={faArrowToTop} />}
                    spacing={buttonSpacing}
                    onClick={onButtonClick}
                    disabled={isUploading}
                    ariaLabel={buttonLabel}
                >
                    {buttonLabel}
                </Button>
            </div>
            {isUploading && (
                <UploadingSpinnerAndText
                    className={`mt-10 ${
                        absolutePositionedSpinner ? 'position-absolute' : ''
                    }`}
                />
            )}
            {isError && absolutePositionedSpinner && <FormError message={uploadError} />}
        </div>
    );
}
